import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

const GP_HOME_HEADER_COLLAPSE_SCROLL_TOP = 16;
const GP_HOME_HEADER_EXPAND_SCROLL_TOP = 1;

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /** @type {HTMLElement | null} */
  #homeScrollAnchorContainer = null;

  #homeScrollAnchorValue = '';

  #homeScrollAnchorPriority = '';

  /** @type {Animation | null} */
  #homeLogoAnimation = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root unless the homepage uses
    // its stable scroll-position state instead of geometry observation.
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    if (this.#usesStableHomeScrollState(stickyMode)) {
      this.#updateHomeScrollAnchoring();
      this.#updateScrollState();
    } else {
      this.#observeStickyPosition(stickyMode === 'always');
    }
  };

  #usesStableHomeScrollState(stickyMode = this.getAttribute('sticky')) {
    return stickyMode === 'always' && this.hasAttribute('data-gp-home-header');
  }

  /**
   * Prevents the homepage header's height transition from changing scrollTop via
   * browser scroll anchoring. The relevant scroll root changes at 990px.
   */
  #updateHomeScrollAnchoring() {
    const scrollContainer = getIntersectionRoot() ?? document.scrollingElement ?? document.documentElement;
    if (!(scrollContainer instanceof HTMLElement) || this.#homeScrollAnchorContainer === scrollContainer) return;

    this.#restoreHomeScrollAnchoring();
    this.#homeScrollAnchorContainer = scrollContainer;
    this.#homeScrollAnchorValue = scrollContainer.style.getPropertyValue('overflow-anchor');
    this.#homeScrollAnchorPriority = scrollContainer.style.getPropertyPriority('overflow-anchor');
    scrollContainer.style.setProperty('overflow-anchor', 'none');
  }

  /** Restores any inline scroll-anchoring value that existed before this component changed it. */
  #restoreHomeScrollAnchoring() {
    if (!this.#homeScrollAnchorContainer) return;

    if (this.#homeScrollAnchorValue) {
      this.#homeScrollAnchorContainer.style.setProperty(
        'overflow-anchor',
        this.#homeScrollAnchorValue,
        this.#homeScrollAnchorPriority,
      );
    } else {
      this.#homeScrollAnchorContainer.style.removeProperty('overflow-anchor');
    }

    this.#homeScrollAnchorContainer = null;
    this.#homeScrollAnchorValue = '';
    this.#homeScrollAnchorPriority = '';
  }

  #updateHomeStickyState(scrollTop) {
    const isCompact = this.dataset.stickyState === 'active';
    const shouldCompact = isCompact
      ? scrollTop > GP_HOME_HEADER_EXPAND_SCROLL_TOP
      : scrollTop >= GP_HOME_HEADER_COLLAPSE_SCROLL_TOP;
    const nextState = shouldCompact ? 'active' : 'inactive';

    if (this.dataset.stickyState === nextState) return;

    const logo = matchMedia('(min-width: 750px)').matches && !matchMedia('(prefers-reduced-motion: reduce)').matches
      ? this.querySelector('.gp-production-header__logo')
      : null;
    const before = logo?.getBoundingClientRect();
    this.#homeLogoAnimation?.cancel();
    this.#homeLogoAnimation = null;
    this.dataset.stickyState = nextState;

    if (logo instanceof HTMLElement && before && before.width > 0 && before.height > 0) {
      const after = logo.getBoundingClientRect();
      if (after.width > 0 && after.height > 0) {
        const styles = getComputedStyle(logo);
        const baseTransform = styles.transform === 'none' ? '' : styles.transform;
        this.#homeLogoAnimation = logo.animate(
          [
            { transform: `translate3d(${before.left - after.left}px, ${before.top - after.top}px, 0) ${baseTransform} scale(${before.width / after.width}, ${before.height / after.height})` },
            { transform: styles.transform },
          ],
          {
            duration: parseFloat(styles.getPropertyValue('--gp-motion-deliberate')) || 280,
            easing: styles.getPropertyValue('--gp-ease-emphasized').trim() || 'cubic-bezier(0.2, 0.8, 0.2, 1)',
          }
        );
      }
    }
    if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const usesStableHomeScrollState = this.#usesStableHomeScrollState(stickyMode);
    if (usesStableHomeScrollState) this.#updateHomeStickyState(scrollTop);

    const headerTop = usesStableHomeScrollState ? 0 : this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = usesStableHomeScrollState ? scrollTop <= GP_HOME_HEADER_EXPAND_SCROLL_TOP : headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      if (this.#usesStableHomeScrollState(stickyMode)) {
        this.#updateHomeScrollAnchoring();
        this.#updateScrollState();
      } else {
        this.#observeStickyPosition(stickyMode === 'always');
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    this.#homeLogoAnimation?.cancel();
    this.#homeLogoAnimation = null;
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    this.#restoreHomeScrollAnchoring();
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

// Anchor the shopify-account dialog to the bottom of the account button itself.
document.addEventListener(
  'open',
  (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;

    const bottom = Math.max(0, Math.round(target.getBoundingClientRect().bottom));
    target.style.setProperty('--account-dialog-top', `${bottom}px`);
  },
  true
);

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
